import React, { useEffect, useState } from "react";
import useApi from "../../hook/useApi";
import Loader from "../../components/Loader/Loader";
import { formatDate } from "../../utils/helper";
import { HOTEL_PAX } from "../../utils/enum";
import { BOOKING_PAGE_TYPE, CURRENCY_CODE, HOTEL_BOOKING_STATUS } from "../../utils/constant";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";

const ViewHotelBookingLayer = ({ id, pageType }) => {
  const { AsyncGetApiCall } = useApi();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    holderName: "",
    holderSurname: "",
    checkIn: "",
    checkOut: "",
  });
  const [cancellationPercent, setCancellationPercent] = useState(1); // Initial 1%
  const [cancellationAmount, setCancellationAmount] = useState(0);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundStatus, setRefundStatus] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBooking = async () => {
      if (!id) {
        setError("Invalid booking ID.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await AsyncGetApiCall(`/hotel-booking/${id}`);
        if (result?.success && result?.data) {
          setBooking(result?.data);
          const holder = result?.data?.hotelBookings?.[0]?.holder || {};
          const hotel = result?.data?.hotelBookings?.[0]?.hotel || {};
          setFormData({
            holderName: holder.name || "",
            holderSurname: holder.surname || "",
            checkIn: hotel.checkIn || "",
            checkOut: hotel.checkOut || "",
          });
          const txData = result?.data?.txData
          setCancellationPercent(txData?.cancellationCharge || 1);
          const cancellationAmount = txData?.paidAmount * (txData.cancellationCharge / 100)
          setCancellationAmount(cancellationAmount || 0);
          setRefundAmount(txData?.refundAmount || 0);
          setRefundStatus(result?.data?.refundStatus);
        } else {
          setError(result?.message || "Failed to load booking details.");
        }
      } catch (err) {
        console.error("API Error:", err); // Debug error
        setError("Error fetching booking: " + (err.message || "Unknown error"));
      } finally {
        setLoading(false); // Ensure loading is reset
      }
    };

    fetchBooking();
  }, [id]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="alert alert-warning" role="alert">
        No booking data found.
      </div>
    );
  }

  // Extract data for rendering
  const holder = booking?.hotelBookings?.[0]?.holder || {};
  const paxes = booking?.hotelBookings?.[0]?.hotel?.rooms?.[0]?.paxes || [];
  const hotel = booking?.hotelBookings?.[0]?.hotel || {};
  const bookingData = booking?.hotelBookings?.[0] || {};
  const rate = bookingData?.hotel?.rates?.[0] || {};
  const policies = bookingData?.modificationPolicies || {};
  const cancellationPolicies = rate?.cancellationPolicies || [];
  const user = booking?.userId;
  const txData = booking?.txData;

  // Validation logic
  const validateDates = () => {
    const today = new Date("2025-08-19");
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const maxDate = new Date(today);
    maxDate.setFullYear(today.getFullYear() + 1); // One year from today

    const checkInDate = formData.checkIn ? new Date(formData.checkIn) : null;
    const checkOutDate = formData.checkOut ? new Date(formData.checkOut) : null;

    // Check if check-in date is before today
    if (checkInDate && checkInDate < today) {
      return "Check-in date cannot be before today.";
    }

    // Check if dates are within one year from today
    if (checkInDate && checkInDate > maxDate) {
      return "Check-in date cannot be more than one year from today.";
    }
    if (checkOutDate && checkOutDate > maxDate) {
      return "Check-out date cannot be more than one year from today.";
    }

    // Check for minimum 1-day gap between check-in and check-out
    if (checkInDate && checkOutDate) {
      const oneDayInMs = 24 * 60 * 60 * 1000;
      if (checkOutDate - checkInDate < oneDayInMs) {
        return "Check-out date must be at least one day after check-in date.";
      }
    }

    // Check if both dates are provided
    if (!checkInDate || !checkOutDate) {
      return "Both check-in and check-out dates are required.";
    }

    return null; // No validation errors
  };

  // Handle Update/Save button click
  const handleSave = () => {
    try {
      setLoading(true);
      const validationError = validateDates();
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const updatedData = {
        holder: {
          name: formData.holderName,
          surname: formData.holderSurname,
        },
        hotel: {
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
        },
      };
      toast.success("Booking data updated successfully.");
      navigate("/hotel-booking")
    } catch (error) {
      console.error("Something went wrong", error);
      toast.error("Failed to update booking data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row gy-4">
      {/* User Info  */}
      <UserInfo user={user} />

      {/* Guest Information */}
      <GuestInfo booking={booking} holder={holder} paxes={paxes} formData={formData}
        setFormData={setFormData} pageType={pageType} />

      {/* Hotel Details */}
      <HotelDetails hotel={hotel} formData={formData} setFormData={setFormData} pageType={pageType} />

      {/* Booking Policies */}
      <BookingPolicies
        cancellationPolicies={cancellationPolicies}
        policies={policies}
        rate={rate}
        bookingData={bookingData}
      />

      {/* Price Summary */}
      <PriceSummary
        rate={rate}
        txData={txData}
        pageType={pageType}
        id={booking?._id}
        cancellationPercent={cancellationPercent}
        cancellationAmount={cancellationAmount}
        setCancellationPercent={setCancellationPercent}
        setCancellationAmount={setCancellationAmount}
        refundStatus={refundStatus}
        setRefundStatus={setRefundStatus}
        refundAmount={refundAmount}
        setRefundAmount={setRefundAmount}
      />

      {/* Update/Save Buttons */}
      {pageType === BOOKING_PAGE_TYPE.EDIT &&
        <div className="col-12 d-flex justify-content-end">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            style={{ marginRight: "10px" }}
          >
            Update
          </button>
        </div>}
    </div>
  );
};

export default ViewHotelBookingLayer;

const UserInfo = ({ user }) => {
  if (!user) {
    return;
  }
  return (
    <div className="col-12">
      <div className="card p-24 radius-12">
        <h5 className="text-md fw-semibold mb-24">User Information</h5>
        <div className="row gy-3">
          <div className="col-md-6">
            <label className="form-label">User Id</label>
            <p className="text-secondary-light">{user?._id || "N/A"}</p>
          </div>

          <div className="col-md-6">
            <label className="form-label">Email</label>
            <p className="text-secondary-light">{user?.email || "N/A"}</p>
          </div>

          <div className="col-md-6">
            <label className="form-label">Created Date</label>
            <p className="text-secondary-light">
              {formatDate(user?.createdAt, true) || "N/A"}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Wallet Address</label>
            <p className="text-secondary-light">
              {user.walletAddress ? user.walletAddress : "N/A"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const GuestInfo = ({ booking, holder, paxes, formData, setFormData, pageType }) => {
  return (
    <div className="col-12">
      <div className="card p-24 radius-12">
        <h5 className="text-md fw-semibold mb-24">Guest Information</h5>
        <div className="row gy-3">
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <p className="text-secondary-light">{booking?.email || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Holder Name</label>
            {pageType === BOOKING_PAGE_TYPE.VIEW || pageType === BOOKING_PAGE_TYPE.REFUND ?
              <p className="text-secondary-light">
                {holder.name && holder.surname
                  ? `${holder.name} ${holder.surname}`
                  : "N/A"}
              </p> :
              <input
                type="text"
                className="form-control"
                value={`${formData.holderName} ${formData.holderSurname}`}
                onChange={(e) => {
                  const [name, ...surnameParts] = e.target.value.split(" ");
                  setFormData({
                    ...formData,
                    holderName: name || "",
                    holderSurname: surnameParts.join(" ") || "",
                  });
                }}
                placeholder="Enter holder name"
              />}
          </div>

          {paxes.length > 0 ? (
            paxes.map((pax, index) => (
              <div key={index} className="col-12">
                <h6 className="text-sm fw-medium">Guest {index + 1}</h6>
                <div className="row gy-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <p className="text-secondary-light">
                      {pax.name && pax.surname
                        ? `${pax.name} ${pax.surname}`
                        : "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Type</label>
                    <p className="text-secondary-light">
                      {HOTEL_PAX[pax.type] || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <p className="text-secondary-light">
                No guest details available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HotelDetails = ({ hotel, formData, setFormData, pageType }) => {
  const today = new Date("2025-08-19");
  today.setHours(0, 0, 0, 0);
  const minDate = today.toISOString().split("T")[0];
  const maxDate = new Date(today);
  maxDate.setFullYear(today.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().split("T")[0];
  return (
    <div className="col-12">
      <div className="card p-24 radius-12">
        <h5 className="text-md fw-semibold mb-24">Hotel Details</h5>
        <div className="row gy-3">
          <div className="col-md-6">
            <label className="form-label">Hotel Name</label>
            <p className="text-secondary-light">{hotel.name || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Category</label>
            <p className="text-secondary-light">
              {hotel.categoryName || "N/A"}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Check-In Date</label>
            {pageType === BOOKING_PAGE_TYPE.VIEW || pageType === BOOKING_PAGE_TYPE.REFUND ?
              <p className="text-secondary-light">
                {hotel.checkIn
                  ? new Date(hotel.checkIn).toLocaleDateString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })
                  : "N/A"}
              </p> :
              <input
                type="date"
                className="form-control"
                value={formData.checkIn ? new Date(formData.checkIn).toISOString().split("T")[0] : ""}
                onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })}
                min={minDate}
                max={maxDateStr}
              />}
          </div>
          <div className="col-md-6">
            <label className="form-label">Check-Out Date</label>
            {pageType === BOOKING_PAGE_TYPE.VIEW || pageType === BOOKING_PAGE_TYPE.REFUND ?
              <p className="text-secondary-light">
                {hotel.checkOut
                  ? new Date(hotel.checkOut).toLocaleDateString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })
                  : "N/A"}
              </p> :
              <input
                type="date"
                className="form-control"
                value={formData.checkOut ? new Date(formData.checkOut).toISOString().split("T")[0] : ""}
                onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })}
                min={minDate}
                max={maxDateStr}
              />
            }
          </div>
          <div className="col-md-6">
            <label className="form-label">Destination</label>
            <p className="text-secondary-light">
              {hotel.destinationName || "N/A"}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Zone</label>
            <p className="text-secondary-light">{hotel.zoneName || "N/A"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingPolicies = ({
  cancellationPolicies,
  policies,
  rate,
  bookingData,
}) => {
  return (
    <div className="col-12">
      <div className="card p-24 radius-12">
        <h5 className="text-md fw-semibold mb-24">Booking Policies</h5>
        <div className="row gy-3">
          <div className="col-md-6">
            <label className="form-label">Cancellation Allowed</label>
            <p className="text-secondary-light">
              {policies.cancellation ? "Yes" : "No"}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Modification Allowed</label>
            <p className="text-secondary-light">
              {policies.modification ? "Yes" : "No"}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label">Booking Status</label>
            <br />
            <p
              className={`badge ${bookingData.status === HOTEL_BOOKING_STATUS.CONFIRMED
                ? "bg-success-100 text-success-600"
                : bookingData.status === HOTEL_BOOKING_STATUS.PENDING
                  ? "bg-warning-100 text-warning-600"
                  : "bg-danger-100 text-danger-600"
                } text-sm py-4 px-10 radius-4`}>
              {bookingData.status}
            </p>
          </div>
          {cancellationPolicies.length > 0 ? (
            <div className="col-12">
              <h6 className="text-sm fw-medium">Cancellation Policies</h6>
              {cancellationPolicies.map((policy, index) => (
                <div key={index} className="mb-3">
                  <p className="text-md">Penalty:</p>
                  <p className="text-secondary-light">
                    {policy.amount} {bookingData.currency} if cancelled after{" "}
                    {policy.from
                      ? new Date(policy.from).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                      : "N/A"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="col-12">
              <p className="text-secondary-light">
                No cancellation policies available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PriceSummary = ({ rate, pageType, txData, id, cancellationPercent, cancellationAmount, setCancellationPercent, setCancellationAmount, refundStatus, setRefundStatus, refundAmount, setRefundAmount }) => {
  const {
    AsyncPostApiCall
  } = useApi();

  const [loading, setLoading] = useState(false);

  // Calculate initial amount based on 1% of paidAmount
  useEffect(() => {
    if (txData?.paidAmount && refundStatus !== "completed") {
      const initialPercent = 1; // Set initial percent to 1
      setCancellationPercent(initialPercent);
      const initialAmount = (txData.paidAmount * initialPercent) / 100; // Calculate amount based on 1%
      setCancellationAmount(Number(initialAmount.toFixed(2)));
      const refundAmount = txData.paidAmount - initialAmount
      setRefundAmount(Number(refundAmount.toFixed(2)));
    }
  }, [txData?.paidAmount, setCancellationAmount]);

  // Handle percentage change with clearing logic
  const handlePercentChange = (e) => {
    const value = e.target.value;
    if (value === '' || value === null) {
      setCancellationPercent(0); // Clear to 0 when empty
      setCancellationAmount(0); // Reset amount when percent is cleared
      setRefundAmount(0);
    } else {
      // Remove extra decimal places and ensure valid number
      const sanitizedValue = Number(value.replace(/[^0-9.]/g, '')).toFixed(2); // Limit to 2 decimal places
      const percent = Math.max(1, Math.min(100, Number(sanitizedValue) || 0));
      setCancellationPercent(percent);
      if (txData?.paidAmount) {
        const newAmount = (txData.paidAmount * percent) / 100;
        setCancellationAmount(Number(newAmount.toFixed(2)));
        const refundAmount = txData.paidAmount - newAmount
        setRefundAmount(Number(refundAmount.toFixed(2)));
      }
    }
  };

  // Handle amount change with clearing logic
  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || value === null) {
      setCancellationAmount(0); // Clear to 0 when empty
      setCancellationPercent(0); // Reset percent when amount is cleared
      setRefundAmount(0);
    } else {
      // Remove extra decimal places and ensure valid number
      const sanitizedValue = Number(value.replace(/[^0-9.]/g, '')).toFixed(2); // Limit to 2 decimal places
      const amount = Math.max(0, Math.min(txData?.paidAmount || 0, Number(sanitizedValue) || 0));
      setCancellationAmount(Number(amount.toFixed(2)));
      if (txData?.paidAmount && txData.paidAmount > 0) {
        const newPercent = (amount / txData.paidAmount) * 100;
        setCancellationPercent(Number(newPercent.toFixed(2)));
        const refundAmount = txData.paidAmount - amount
        setRefundAmount(Number(refundAmount.toFixed(2)));
      }
    }
  };

  // Toggle edit mode and mark as used
  const handleRefund = async () => {
    try {
      if (cancellationPercent < 1) {
        toast.error("Cancellation charge must be between 1 and 100");
        return;
      }
      setLoading(true);
      const payload = {
        id: id,
        model: "HOTEL",
        cancellationCharge: cancellationPercent
      };

      const result = await AsyncPostApiCall(`/hotel-booking/initiate-refund`, payload);

      if (result?.success) {
        toast.success(result.message);
        const txData = result?.data?.txData
        setCancellationPercent(txData?.cancellationCharge || 1);
        const cancellationAmount = txData?.paidAmount * (txData.cancellationCharge / 100)
        setCancellationAmount(cancellationAmount || 0);
        setRefundAmount(txData?.refundAmount || 0);
        setRefundStatus(result?.data?.refundStatus);
      } else {
        toast.error(result?.errors?.[0] || "Something went wrong");
      }
    } catch (error) {
      console.error("Something went wrong", error);
      toast.error("Failed to update booking data.");
    } finally {
      setLoading(false);
    }
  };

  // Cleanup effect to clear inputs when leaving the page
  useEffect(() => {
    return () => {
      setCancellationPercent(0); // Clear percent when component unmounts
      setCancellationAmount(0); // Clear amount when component unmounts
    };
  }, [setCancellationPercent, setCancellationAmount]);

  return (
    <>
      {loading && <Loader />}
      <div className="col-12">
        <div className="card p-24 radius-12">
          <h5 className="text-md fw-semibold mb-24">Price Summary</h5>
          <div className="row gy-3">
            <div className="col-md-6">
              <label className="form-label">Paid Amount</label>
              <p className="text-secondary-light">
                {`${txData?.paidAmount ? CURRENCY_CODE[txData?.paidCurrency] : ""} ${Number(txData?.paidAmount).toFixed(2) || "N/A"
                  }`}
              </p>
            </div>
            <div className="col-md-6">
              <label className="form-label">Payment Method</label>
              <p className="text-secondary-light">
                {txData?.paymentMethod.toUpperCase() || "N/A"}
              </p>
            </div>
            <div className="col-md-6">
              <label className="form-label">Original Amount</label>
              <p className="text-secondary-light">
                {`${txData?.originalAmount ? CURRENCY_CODE[txData?.originalCurrency] : ""} ${Number(txData?.originalAmount).toFixed(2) || "N/A"
                  }`}
              </p>
            </div>
            {txData?.coupon?.couponApplied && txData?.couponAmount ? (
              <div className="col-md-6">
                <label className="form-label">Discounts</label>
                <p className="text-secondary-light">
                  - {CURRENCY_CODE[txData?.paidCurrency]} {txData?.couponAmount}
                </p>
              </div>
            ) : (
              <div className="col-md-6">
                <label className="form-label">Discounts</label>
                <p className="text-secondary-light">No discounts applied.</p>
              </div>
            )}
            {txData?.rewardDetails ? (
              <div className="col-md-6">
                <label className="form-label">Rewards</label>
                <p className="text-secondary-light">
                  - {CURRENCY_CODE[txData?.rewardDetails?.pointsCurrency]} {txData?.rewardDetails?.pointsValue?.toFixed(2)}
                </p>
              </div>
            ) : (
              <div className="col-md-6">
                <label className="form-label">Rewards</label>
                <p className="text-secondary-light">No rewards applied.</p>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label">Rate Comments</label>
              <p className="text-secondary-light">{rate.rateComments || "N/A"}</p>
            </div>
            {(pageType === BOOKING_PAGE_TYPE.REFUND || refundStatus === "completed") && (
              <div className="col-md-6">
                <label className="form-label">Refund Status</label>
                <br />
                <p className={`badge ${refundStatus === 'completed'
                  ? 'bg-success-100 text-success-600'
                  : refundStatus === 'pending'
                    ? 'bg-warning-100 text-warning-600'
                    : refundStatus === 'not_applicable'
                      ? 'bg-primary-300 text-secondary-600'
                      : refundStatus === 'failed' || refundStatus === 'rejected'
                        ? 'bg-danger-100 text-danger-600'
                        : 'background-gray-100'
                  } text-sm py-4 px-10 radius-4`}>{refundStatus === 'not_applicable' ? "NOT APPLICABLE" : refundStatus.toUpperCase()}</p>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label" style={{ marginBottom: '5px' }}>
                {refundStatus === "completed" ? "Initiated Refund (Including Charges)" : "Initiate Refund (Apply Cancellation Charge)"}
              </label>
              <div className="card" style={{ padding: '15px', marginBottom: '15px', margin: '0 auto' }}>
                <div className="d-flex flex-column align-items-start" style={{ marginBottom: '10px' }}>
                  <span className="text-secondary-light" style={{ marginBottom: '5px', fontSize: '14px', color: '#6c757d' }}>Charges:</span>
                  <div className="input-group flex-nowrap" style={{ width: '50%', padding: '5px 0' }}>
                    <input
                      type="number"
                      className="form-control"
                      value={cancellationPercent || ''}
                      onChange={handlePercentChange}
                      min="1"
                      max="100"
                      step="0.01"
                      onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                      placeholder="%"
                      style={{ maxWidth: '80px', padding: '6px 10px', fontSize: '14px' }}
                      disabled={refundStatus === "completed"}
                    />
                    <span className="input-group-text" style={{ padding: '6px 10px', backgroundColor: '#f8f9fa', border: '1px solid #ced4da' }}>%</span>
                    <input
                      type="number"
                      className="form-control"
                      value={refundStatus === "completed" ? cancellationAmount.toFixed(2) : cancellationAmount || ''}
                      onChange={handleAmountChange}
                      min="0"
                      max={txData?.paidAmount || 2000}
                      step="0.01"
                      onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                      placeholder="Amount"
                      style={{ maxWidth: '100px', padding: '6px 10px', fontSize: '14px' }}
                      disabled={refundStatus === "completed"}
                    />
                    <span className="input-group-text" style={{ padding: '6px 10px', backgroundColor: '#f8f9fa', border: '1px solid #ced4da' }}>
                      {txData?.paidCurrency ? CURRENCY_CODE[txData.paidCurrency] : ""}
                    </span>
                  </div>
                  <span className="text-secondary-light" style={{ marginBottom: '5px', fontSize: '14px', color: '#6c757d' }}>Refund Amount:</span>
                  <div className="input-group flex-nowrap" style={{ width: '50%', padding: '5px 0' }}>
                    <input
                      type="number"
                      className="form-control"
                      value={refundAmount.toFixed(2)}
                      style={{ maxWidth: '180px', padding: '6px 10px', fontSize: '14px' }}
                      disabled
                    />
                  </div>
                </div>
                <span className="text-secondary-light" style={{ marginBottom: '5px', fontSize: '14px', color: '#6c757d' }}>User have paid {txData?.paidAmount} {txData?.paidCurrency} for this booking, after deducting {cancellationAmount.toFixed(2)} {txData?.paidCurrency} ({cancellationPercent}%) as cancellation charge, {refundAmount.toFixed(2)} {txData?.paidCurrency} is the refunded amount.</span>
                <div className="d-flex justify-content-center" style={{ marginTop: '15px' }}>
                  {refundStatus !== "completed" && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to initiate refund?")) { handleRefund() }
                      }}
                      disabled={refundStatus === "completed" || cancellationPercent < 1}
                      style={{ minWidth: '100px', padding: '6px 15px' }}
                    >
                      Submit
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  );
};
